import React from 'react';
import { useSelector } from 'react-redux';
import {
  MenuItem,
  Box,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import brandingConfig from '../../config/brandingConfig';
import { isCriticalNotification } from '../../redux/slices/notificationsSlice';

const NotificationCenter = ({ onClose }) => {
  const navigate = useNavigate();
  
  const { notifications, unreadCount } = useSelector((state) => state.notifications);

  const formatNotificationDate = (value) => {
    if (!value) return '';
    
    try {
      const date = value?.toDate ? value.toDate() : new Date(value);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Gerade eben';
      if (diffInMinutes < 60) return `vor ${diffInMinutes} Min`;
      if (diffInMinutes < 1440) return `vor ${Math.floor(diffInMinutes / 60)} Std`;
      
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  const handleNotificationClick = (notification) => {
    const rideId = notification.data?.rideId || notification.data?.ride_id;
    const url = notification.data?.url || (rideId ? `/fahrten/${rideId}` : null);
    if (url) {
      navigate(url);
      if (onClose) onClose();
    }
  };

  const getNotificationIcon = (type, priority) => {
    switch (type) {
      // Kritisch: sofortiger Handlungsbedarf des Disponenten
      case 'overdue_ride':
      case 'portal_dispatch_failed':
      case 'portal_dispatch_no_candidates':
      case 'portal_ride_auto_cancelled':
      case 'portal_assignment_rejected':
      case 'portal_preorder_unassigned':
      case 'portal_preorder_not_started':
      case 'portal_medical_quota_exhausted':
      case 'portal_driver_license_expired':
      case 'portal_vehicle_deadline_expired':
      case 'portal_driver_auto_logout':
        return <WarningIcon sx={{ color: brandingConfig.colors.error }} />;
      // Warnung: wichtig, aber läuft weiter bzw. keine Aktion nötig
      case 'portal_ride_cancelled_by_customer':
      case 'portal_ride_cancelled_by_driver':
      case 'portal_ride_cancelled':
      case 'portal_assignment_mismatch':
      case 'portal_ride_returned_by_driver':
      case 'portal_preorder_returned':
      case 'portal_preorder_gegenvorschlag_expired':
      case 'portal_medical_quota_low':
      case 'portal_medical_order_expiring':
      case 'portal_medical_ride_converted':
      case 'portal_driver_license_expiring':
      case 'portal_vehicle_deadline_expiring':
        return <WarningIcon sx={{ color: brandingConfig.colors.warning }} />;
      case 'central_message':
        return priority === 'high' ?
          <WarningIcon sx={{ color: brandingConfig.colors.warning }} /> :
          <InfoIcon sx={{ color: brandingConfig.colors.info }} />;
      default:
        // Unbekannte Typen nach mitgelieferter Priorität einordnen
        if (priority === 'critical') {
          return <WarningIcon sx={{ color: brandingConfig.colors.error }} />;
        }
        if (priority === 'high') {
          return <WarningIcon sx={{ color: brandingConfig.colors.warning }} />;
        }
        return <InfoIcon sx={{ color: brandingConfig.colors.info }} />;
    }
  };

  const criticalNotifications = notifications?.filter(n => isCriticalNotification(n) && !n.read) || [];
  const regularNotifications = notifications?.filter(n => !(isCriticalNotification(n) && !n.read)) || [];

  return (
    <>
      {/* Header */}
      <Box key="header" sx={{ p: 2, backgroundColor: brandingConfig.colors.primary, color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            Benachrichtigungen
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small" 
                sx={{ 
                  backgroundColor: 'white', 
                  color: brandingConfig.colors.primary,
                  fontWeight: 'bold'
                }} 
              />
            )}
          </Typography>
        </Box>
      </Box>

      {/* Kritische Meldungen (ungelesen) */}
      {criticalNotifications.length > 0 && (
        <>
          <Box key="critical-header" sx={{ p: 1, backgroundColor: brandingConfig.colors.error + '10' }}>
            <Typography variant="subtitle2" sx={{ color: brandingConfig.colors.error, fontWeight: 'bold' }}>
              🚨 Kritische Meldungen ({criticalNotifications.length})
            </Typography>
          </Box>

          {criticalNotifications.slice(0, 3).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderLeft: `4px solid ${brandingConfig.colors.error}`,
                backgroundColor: brandingConfig.colors.error + '05',
                '&:hover': {
                  backgroundColor: brandingConfig.colors.error + '15'
                }
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type, notification.data?.priority)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: brandingConfig.colors.error }}>
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" sx={{ color: 'text.primary', display: 'block' }}>
                      {notification.body}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {formatNotificationDate(notification.createdAt)}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          ))}
          
          <Divider key="critical-divider" />
        </>
      )}

      {/* Regular Notifications Section */}
      {regularNotifications.length > 0 ? (
        <>
          {criticalNotifications.length > 0 && (
            <Box key="regular-header" sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Weitere Benachrichtigungen
              </Typography>
            </Box>
          )}
          
          {regularNotifications.slice(0, 4).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderLeft: notification.read ? 'none' : `4px solid ${brandingConfig.colors.primary}`
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type, notification.data?.priority)}
              </ListItemIcon>
              <ListItemText
                primary={notification.title}
                secondary={
                  <>
                    <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                      {notification.body}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {formatNotificationDate(notification.createdAt)}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          ))}
        </>
      ) : criticalNotifications.length === 0 ? (
        <MenuItem key="no-notifications" disabled>
          <ListItemText 
            primary="Keine Benachrichtigungen"
            secondary="Alle Benachrichtigungen sind gelesen"
          />
        </MenuItem>
      ) : null}

      {/* Footer */}
      {notifications?.length > 7 && (
        <>
          <Divider key="footer-divider" />
          <MenuItem key="all-notifications">
            <ListItemIcon>
              <VisibilityIcon />
            </ListItemIcon>
            <ListItemText 
              primary={`Alle ${notifications?.length || 0} Benachrichtigungen anzeigen`}
            />
          </MenuItem>
        </>
      )}
    </>
  );
};

export default NotificationCenter;
